import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import api from "@/services/api";

// Default permission sets used when the backend hasn't stored them yet for a role.
// Keyed by role value; each entry lists the modules that are enabled and which actions.
const ROLE_PERMISSION_DEFAULTS: Record<
  string,
  { module: string; actions: string[] }[]
> = {
  manager: [
    { module: "dashboard", actions: ["view"] },
    { module: "debtors", actions: ["view", "edit"] },
    { module: "cases", actions: ["view", "edit"] },
    { module: "payments", actions: ["view", "create", "edit"] },
    { module: "agents", actions: ["view"] },
    { module: "reports", actions: ["view"] },
  ],
  team_leader: [
    { module: "dashboard", actions: ["view"] },
    { module: "debtors", actions: ["view", "edit"] },
    { module: "cases", actions: ["view", "edit"] },
    { module: "payments", actions: ["view", "create"] },
    { module: "agents", actions: ["view"] },
    { module: "reports", actions: ["view"] },
    { module: "agent_portal", actions: ["view"] },
  ],
  agent: [
    { module: "agent_portal", actions: ["view"] },
    { module: "cases", actions: ["view", "edit"] },
    { module: "payments", actions: ["view", "create"] },
  ],
};

const normalizeModuleAction = (action: any, enabled: boolean) => {
  if (typeof action === "string") {
    return {
      key: action,
      label: action.charAt(0).toUpperCase() + action.slice(1),
      enabled,
    };
  }

  const key = action?.key ?? "";
  return {
    ...action,
    key,
    label:
      action?.label ?? (key ? key.charAt(0).toUpperCase() + key.slice(1) : ""),
    enabled,
  };
};

interface ModulePermission {
  module: string;
  label: string;
  actions: { key: string; label: string; enabled: boolean }[];
}

interface PermissionsState {
  modules: any[];
  roles: any[];
  rolePermissions: Record<string, ModulePermission[]>;
  loading: boolean;
  error: string | null;
  currentRole: string;
}

const REQUIRED_ROLES = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager / Regional Head" },
  { value: "team_leader", label: "Team Leader" },
  { value: "agent", label: "Agent" },
  { value: "telecaller", label: "Telecaller" },
];

const initialState: PermissionsState = {
  modules: [],
  roles: REQUIRED_ROLES,
  rolePermissions: {},
  loading: false,
  error: null,
  currentRole: "",
};

export const fetchModules = createAsyncThunk(
  "permissions/fetchModules",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/v1/settings/role-permissions/modules");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch modules",
      );
    }
  },
);

export const fetchRoles = createAsyncThunk(
  "permissions/fetchRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/v1/settings/role-permissions/roles");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch roles",
      );
    }
  },
);

export const fetchPermissions = createAsyncThunk(
  "permissions/fetchPermissions",
  async (role: string, { rejectWithValue }) => {
    try {
      const response = await api.get("/v1/settings/role-permissions", {
        params: { role },
      });
      return { role, permissions: response.data };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch permissions",
      );
    }
  },
);

export const updatePermissions = createAsyncThunk(
  "permissions/updatePermissions",
  async (
    { role, permissions }: { role: string; permissions: any[] },
    { rejectWithValue },
  ) => {
    try {
      // Transform the permissions format to match backend expectations
      const transformedPermissions = permissions.map((perm) => {
        const actionsArray = Array.isArray(perm.actions) ? perm.actions : [];

        return {
          module: perm.module,
          actions: actionsArray
            .filter((action: any) => action.enabled)
            .map((action: any) => action.key),
        };
      });
      console.log(
        "FINAL PAYLOAD:",
        JSON.stringify(
          {
            role,
            permissions: transformedPermissions,
          },
          null,
          2,
        ),
      );

      const response = await api.post("/v1/settings/role-permissions", {
        role,
        permissions: transformedPermissions,
      });
      return { role, permissions: transformedPermissions };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update permissions",
      );
    }
  },
);

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    setCurrentRole: (state, action) => {
      state.currentRole = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchModules.fulfilled, (state, action) => {
        state.modules = action.payload;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        const roles = Array.isArray(action.payload) ? action.payload : [];
        // Merge backend roles with required roles, keeping backend labels when present
        // and filtering out super_admin (managed at platform level)
        const backendMap = new Map(roles.map((r: any) => [r.value, r]));
        state.roles = REQUIRED_ROLES.map(
          (req) => backendMap.get(req.value) ?? req,
        );
        if (!state.currentRole && state.roles.length > 0) {
          state.currentRole = state.roles[0].value;
        }
      })
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.rolePermissions[action.payload.role] = action.payload.permissions;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updatePermissions.fulfilled, (state, action) => {
        const existingModules = state.modules;

        const normalized = existingModules.map((module: any) => {
          const updated = action.payload.permissions.find(
            (p: any) => p.module === module.module,
          );

          return {
            ...module,
            actions: module.actions.map((a: any) => ({
              ...a,
              enabled: updated?.actions?.includes(a.key) || false,
            })),
          };
        });

        state.rolePermissions[action.payload.role] = normalized;
      });
  },
});

export const { setCurrentRole } = permissionsSlice.actions;

const selectPermissionsState = (state: any) => state.permissions;
const selectCurrentRole = (state: any) => state.permissions.currentRole;

export const selectModules = createSelector(
  [selectPermissionsState],
  (state) => state.modules,
);

export const selectRoles = createSelector(
  [selectPermissionsState],
  (state) => state.roles,
);

export const selectCurrentPermissions = createSelector(
  [selectPermissionsState, selectCurrentRole],
  (state, currentRole) => {
    const stored = state.rolePermissions[currentRole];
    if (stored && stored.length > 0) return stored;

    // Fall back to defaults built from the modules list so labels are correct
    const defaults = ROLE_PERMISSION_DEFAULTS[currentRole];
    if (!defaults || state.modules.length === 0) return [];

    return state.modules.map((mod: any) => {
      const entry = defaults.find((d) => d.module === mod.module);
      return {
        ...mod,
        actions: (mod.actions ?? []).map((a: any) => ({
          ...normalizeModuleAction(
            a,
            entry
              ? entry.actions.includes(typeof a === "string" ? a : a?.key)
              : false,
          ),
        })),
      };
    });
  },
);

export const selectPermissionsLoading = createSelector(
  [selectPermissionsState],
  (state) => state.loading,
);

export const selectPermissionsError = createSelector(
  [selectPermissionsState],
  (state) => state.error,
);

export default permissionsSlice.reducer;
